/**
 * QA Metrics Component
 * Displays QA metrics dashboard
 */
import { useState, useEffect } from 'react'
import { Award, TrendingUp, Target, Zap } from 'lucide-react'
import { programQAService, QAMetrics } from '../../services/programQA'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import type { ApiErrorLike } from '../../utils/apiError'

interface QAMetricsProps {
  programId: number
}

export default function QAMetricsComponent({ programId }: QAMetricsProps) {
  const [metrics, setMetrics] = useState<QAMetrics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadMetrics()
  }, [programId])

  const loadMetrics = async () => {
    setLoading(true)
    try {
      const data = await programQAService.getMetrics(programId)
      setMetrics(data)
    } catch (errorRaw) {
      const error = errorRaw as ApiErrorLike
      // Metrics may not be computed for a newly-created program — the
      // empty-state UI handles `null` below. Log so dev tools still
      // surface unexpected errors (auth, network, etc).
      console.warn('QAMetrics loadMetrics failed; showing empty state:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-xl border-2 border-[#bcccdc] shadow-md p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-[#f0f4f8] rounded w-1/3 mb-4"></div>
          <div className="grid grid-cols-2 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-24 bg-[#f0f4f8] rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (!metrics) {
    return (
      <div className="bg-white rounded-xl border-2 border-[#bcccdc] shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-[#102a43]">QA Metrics</h3>
          <button className="px-4 py-2 bg-[#486581] text-white rounded-lg hover:bg-[#5a7692] transition-colors text-sm">
            Calculate Metrics
          </button>
        </div>
        <p className="text-[#829ab1]">No QA metrics available. Click "Calculate Metrics" to generate.</p>
      </div>
    )
  }

  const chartData = [
    {
      name: 'Test Coverage',
      value: metrics.test_coverage || 0,
      target: 80
    },
    {
      name: 'Test Execution',
      value: metrics.test_execution || 0,
      target: 90
    },
    {
      name: 'Automation Ratio',
      value: metrics.automation_ratio || 0,
      target: 70
    }
  ]

  return (
    <div className="bg-white rounded-xl border-2 border-[#bcccdc] shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-[#102a43]">QA Metrics</h3>
        <span className="text-xs text-[#829ab1]">
          Calculated: {new Date(metrics.calculated_at).toLocaleDateString()}
        </span>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="p-4 bg-[#f0f4f8] rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Target className="w-4 h-4 text-[#486581]" />
            <span className="text-sm text-[#829ab1]">Test Coverage</span>
          </div>
          <div className="text-2xl font-bold text-[#102a43]">
            {metrics.test_coverage?.toFixed(1) || 0}%
          </div>
        </div>

        <div className="p-4 bg-[#f0f4f8] rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Award className="w-4 h-4 text-[#486581]" />
            <span className="text-sm text-[#829ab1]">Test Execution</span>
          </div>
          <div className="text-2xl font-bold text-[#102a43]">
            {metrics.test_execution?.toFixed(1) || 0}%
          </div>
        </div>

        <div className="p-4 bg-[#f0f4f8] rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Zap className="w-4 h-4 text-[#486581]" />
            <span className="text-sm text-[#829ab1]">Automation Ratio</span>
          </div>
          <div className="text-2xl font-bold text-[#102a43]">
            {metrics.automation_ratio?.toFixed(1) || 0}%
          </div>
        </div>

        <div className="p-4 bg-[#f0f4f8] rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-[#486581]" />
            <span className="text-sm text-[#829ab1]">Defect Density</span>
          </div>
          <div className="text-2xl font-bold text-[#102a43]">
            {metrics.defect_density?.toFixed(2) || 0}
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="mt-6">
        <h4 className="text-sm font-semibold text-[#102a43] mb-4">Metrics Overview</h4>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis 
              dataKey="name" 
              stroke="#829ab1"
              style={{ fontSize: '12px' }}
            />
            <YAxis 
              stroke="#829ab1"
              style={{ fontSize: '12px' }}
              domain={[0, 100]}
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#fff', 
                border: '1px solid #bcccdc',
                borderRadius: '8px'
              }}
              formatter={(value: number) => `${value.toFixed(1)}%`}
            />
            <Bar 
              dataKey="value" 
              fill="#486581" 
              name="Current"
              radius={[4, 4, 0, 0]}
            />
            <Bar 
              dataKey="target" 
              fill="#bcccdc" 
              name="Target"
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

